import { useEffect } from 'react';
import './BraveBirdAnimation.css';

/**
 * PROFESSIONAL BRAVE BIRD ANIMATION
 * Flying-type physical move with high-power aerial dive and recoil damage
 * 
 * Key Features:
 * - Flying-type physical attack (120 base power, 100% accuracy)
 * - User takes 1/3 of damage dealt as recoil
 * - High-altitude aerial dive with wind effects
 * - Professional gaming company quality with aerial timing
 * - Physical move animation with dive-bomb mechanics and recoil aftermath
 */
export default function BraveBirdAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate dive trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    // PHASE 1: High-Altitude Ascent (0-600ms)
    console.log('Brave Bird Phase 1: High-Altitude Ascent');
    
    // Player ascent with wind aura
    playerEl.classList.add('brave-bird-user-ascent');
    
    // Wind charging aura around player
    const windAura = document.createElement('div');
    windAura.className = 'brave-bird-wind-aura';
    windAura.style.left = `${playerX - 100}px`;
    windAura.style.top = `${playerY - 100}px`;
    windAura.style.width = '200px';
    windAura.style.height = '200px';
    container.appendChild(windAura);
    elements.push(windAura);

    // Flying energy particles around player (representing altitude gain)
    for (let i = 0; i < 18; i++) {
      const windParticle = document.createElement('div');
      windParticle.className = 'brave-bird-wind-particle';
      const angle = (i / 18) * 360;
      const radius = 70 + (i % 3) * 15;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      windParticle.style.left = `${particleX - 6}px`;
      windParticle.style.top = `${particleY - 6}px`;
      windParticle.style.animationDelay = `${i * 30}ms`;
      windParticle.textContent = '💨';
      container.appendChild(windParticle);
      elements.push(windParticle);
    }

    // Altitude indicators showing height gain
    for (let i = 0; i < 8; i++) {
      const altitudeIndicator = document.createElement('div');
      altitudeIndicator.className = 'brave-bird-altitude-indicator';
      altitudeIndicator.style.left = `${playerX + (i % 4 === 0 ? -40 : 40)}px`;
      altitudeIndicator.style.top = `${playerY - (i * 15) - 30}px`;
      altitudeIndicator.style.animationDelay = `${i * 60}ms`;
      altitudeIndicator.textContent = '⬆️';
      container.appendChild(altitudeIndicator);
      elements.push(altitudeIndicator);
    }

    // PHASE 2: Aerial Dive Bomb (600-1000ms)
    const phase2Timer = setTimeout(() => {
      console.log('Brave Bird Phase 2: Aerial Dive Bomb');
      
      // Player dive movement toward opponent
      playerEl.classList.remove('brave-bird-user-ascent');
      playerEl.classList.add('brave-bird-user-dive');
      playerEl.style.setProperty('--dive-x', `${dx}px`);
      playerEl.style.setProperty('--dive-y', `${dy}px`);
      playerEl.style.setProperty('--dive-angle', `${angle}deg`);

      // Create dive trail effects
      for (let i = 0; i < 12; i++) {
        const trailTimer = setTimeout(() => {
          const diveTrail = document.createElement('div');
          diveTrail.className = 'brave-bird-dive-trail';
          const progress = i / 12;
          const trailX = playerX + (dx * progress) + (Math.random() - 0.5) * 25;
          const trailY = playerY + (dy * progress) + (Math.random() - 0.5) * 25;
          diveTrail.style.left = `${trailX - 4}px`;
          diveTrail.style.top = `${trailY - 4}px`;
          diveTrail.style.transform = `rotate(${angle + 90}deg)`;
          diveTrail.textContent = '🌪️';
          container.appendChild(diveTrail);
          elements.push(diveTrail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Wind pressure effects during dive
      for (let i = 0; i < 10; i++) {
        const pressureTimer = setTimeout(() => {
          const windPressure = document.createElement('div');
          windPressure.className = 'brave-bird-wind-pressure';
          const pressureX = playerX + (dx * (i / 10)) + (Math.random() - 0.5) * 40;
          const pressureY = playerY + (dy * (i / 10)) + (Math.random() - 0.5) * 30;
          windPressure.style.left = `${pressureX - 8}px`;
          windPressure.style.top = `${pressureY - 8}px`;
          windPressure.textContent = '💨';
          container.appendChild(windPressure);
          elements.push(windPressure);
        }, i * 30);
        elements.push({ timer: pressureTimer, type: 'timer' });
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: High-Impact Collision (1000-1500ms)
    const phase3Timer = setTimeout(() => {
      console.log('Brave Bird Phase 3: High-Impact Collision');
      
      // Opponent hit reaction
      opponentEl.classList.add('brave-bird-opponent-hit');

      // Reset player position with impact effect
      playerEl.classList.remove('brave-bird-user-dive');
      playerEl.classList.add('brave-bird-user-impact');

      // Massive aerial impact explosion
      const impactExplosion = document.createElement('div');
      impactExplosion.className = 'brave-bird-impact-explosion';
      impactExplosion.style.left = `${opponentX - 140}px`;
      impactExplosion.style.top = `${opponentY - 140}px`;
      impactExplosion.style.width = '280px';
      impactExplosion.style.height = '280px';
      container.appendChild(impactExplosion);
      elements.push(impactExplosion);

      // Wind burst particles from impact
      for (let i = 0; i < 20; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'brave-bird-wind-burst';
        const burstAngle = (i / 20) * 360;
        const burstRadius = 80 + Math.random() * 50;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${burstX - 6}px`;
        burstParticle.style.top = `${burstY - 6}px`;
        burstParticle.style.animationDelay = `${i * 20}ms`;
        burstParticle.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * 40}px`);
        burstParticle.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * 40}px`);
        burstParticle.textContent = '💨';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }

      // Aerial shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'brave-bird-shockwave';
          shockwave.style.left = `${opponentX - 100}px`;
          shockwave.style.top = `${opponentY - 100}px`;
          shockwave.style.width = '200px';
          shockwave.style.height = '200px';
          shockwave.style.animationDelay = `${i * 120}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 150);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Recoil Damage (1500-1900ms)
    const phase4Timer = setTimeout(() => {
      console.log('Brave Bird Phase 4: Recoil Damage');
      
      // Remove impact effects
      playerEl.classList.remove('brave-bird-user-impact');
      playerEl.classList.add('brave-bird-user-recoil');
      
      // Recoil damage effect on player (1/3 damage taken)
      const recoilEffect = document.createElement('div');
      recoilEffect.className = 'brave-bird-recoil-effect';
      recoilEffect.style.left = `${playerX - 80}px`;
      recoilEffect.style.top = `${playerY - 80}px`;
      recoilEffect.style.width = '160px';
      recoilEffect.style.height = '160px';
      container.appendChild(recoilEffect);
      elements.push(recoilEffect);

      // Recoil damage particles around player
      for (let i = 0; i < 12; i++) {
        const recoilParticle = document.createElement('div');
        recoilParticle.className = 'brave-bird-recoil-particle';
        const recoilX = playerX + (Math.random() - 0.5) * 100;
        const recoilY = playerY + (Math.random() - 0.5) * 80;
        recoilParticle.style.left = `${recoilX - 4}px`;
        recoilParticle.style.top = `${recoilY - 4}px`;
        recoilParticle.style.animationDelay = `${i * 40}ms`;
        container.appendChild(recoilParticle);
        elements.push(recoilParticle);
      }

      // Exhaustion effect showing recoil fatigue
      const exhaustionEffect = document.createElement('div');
      exhaustionEffect.className = 'brave-bird-exhaustion';
      exhaustionEffect.style.left = `${playerX + 30}px`;
      exhaustionEffect.style.top = `${playerY - 30}px`;
      container.appendChild(exhaustionEffect);
      elements.push(exhaustionEffect);
    }, 1500);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1900ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Brave Bird: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('brave-bird-user-ascent', 'brave-bird-user-dive', 'brave-bird-user-impact', 'brave-bird-user-recoil');
      opponentEl.classList.remove('brave-bird-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1900);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('brave-bird-user-ascent', 'brave-bird-user-dive', 'brave-bird-user-impact', 'brave-bird-user-recoil');
      opponentEl.classList.remove('brave-bird-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
BraveBirdAnimation.moveType = 'flying';
